/**
 * 간소화된 인증 컨텍스트
 * SimpleVsCodeAuthHandler를 사용하여 복잡성 제거
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { IIdeMessagingService } from '@edusense/shared-types-ide';
import type { AuthState, User } from '@edusense/shared-types-auth';
// User 타입은 types-auth에서 import (types-user에는 UserProfile만 있음)

import { isVSCodeEnvironment } from '../utils/navigation';
import { SimpleVsCodeAuthHandler } from '../handlers/simple-vscode';
// WebAuthHandler 제거 - 사용하지 않음
// StatusDisplay 제거 - FSD 아키텍처: shared layer에서 UI 컴포넌트 사용 금지

// 인증 컨텍스트 타입 정의
interface SimpleAuthContextType extends AuthState {
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  fetchUserEmail: () => Promise<string | undefined>;
}

// 기본값
const defaultAuthContext: SimpleAuthContextType = {
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: true,
  error: null,
  logout: async () => { console.warn('AuthProvider not initialized'); },
  refreshAuth: async () => { console.warn('AuthProvider not initialized'); },
  fetchUserEmail: async () => { console.warn('AuthProvider not initialized'); return undefined; },
};

export const SimpleAuthContext = createContext<SimpleAuthContextType>(defaultAuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
  ideMessagingService?: IIdeMessagingService;
}

export const SimpleAuthProvider: React.FC<AuthProviderProps> = ({ children, ideMessagingService }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    isLoading: true,
    error: null
  });

  const authHandlerRef = useRef<SimpleVsCodeAuthHandler | null>(null);
  const ideMessengerInstance = ideMessagingService || null;
  const isVSCodeEnv = isVSCodeEnvironment();

  // VSCode 환경에서만 핸들러 초기화
  useEffect(() => {
    if (isVSCodeEnv && ideMessengerInstance) {
      console.log('[SimpleAuthProvider] VSCode 환경 - SimpleVsCodeAuthHandler 초기화');
      
      const handler = new SimpleVsCodeAuthHandler(ideMessengerInstance);
      authHandlerRef.current = handler;

      // 상태 변경 리스너 등록
      handler.addStateChangeListener((newState) => {
        console.log('[SimpleAuthProvider] 인증 상태 변경:', newState);
        setAuthState(newState);
      });

      // 초기화
      handler.initialize().catch(error => {
        console.error('[SimpleAuthProvider] 핸들러 초기화 실패:', error);
      });

      return () => {
        // 정리
        if (authHandlerRef.current) {
          authHandlerRef.current.removeStateChangeListener(() => {});
        }
      };
    } else if (!isVSCodeEnv) {
      // 웹 환경에서는 기존 WebAuthHandler 사용 (간소화 필요시 별도 작업)
      console.log('[SimpleAuthProvider] 웹 환경 - WebAuthHandler 사용 (향후 간소화 예정)');
      setAuthState(prev => ({ ...prev, isLoading: false }));
    } else {
      console.warn('[SimpleAuthProvider] VSCode 환경이지만 IdeMessenger가 없습니다.');
      setAuthState(prev => ({ ...prev, isLoading: false, error: 'VSCode 환경 초기화 실패' }));
    }
  }, [isVSCodeEnv, ideMessengerInstance]);

  // VSCode 세션 변경 이벤트 리스너
  useEffect(() => {
    if (!ideMessengerInstance || !isVSCodeEnv) return;

    const handleSessionChange = async (data: any) => {
      console.log('[SimpleAuthProvider] VSCode 세션 변경 이벤트 수신:', data);
      
      if (authHandlerRef.current) {
        try {
          await authHandlerRef.current.refreshAuthState();
        } catch (error) {
          console.error('[SimpleAuthProvider] 세션 변경 처리 중 오류:', error);
        }
      }
    };

    // Register listener with ideMessagingService if available
    const cleanup = ideMessengerInstance.onMessage?.('didChangeControlPlaneSessionInfo', handleSessionChange);
    return cleanup;
  }, [ideMessengerInstance, isVSCodeEnv]);

  // 로그아웃 함수
  const logout = useCallback(async () => {
    console.log('[SimpleAuthProvider] 로그아웃 시작');
    
    if (authHandlerRef.current) {
      await authHandlerRef.current.logout();
    } else if (!isVSCodeEnv) {
      // 웹 환경에서는 WebAuthHandler 사용
      // 향후 웹용 간소화된 핸들러로 교체 예정
      console.log('[SimpleAuthProvider] 웹 환경 로그아웃 (WebAuthHandler 사용)');
    }
  }, [isVSCodeEnv]);

  // 인증 새로고침 함수
  const refreshAuth = useCallback(async () => {
    console.log('[SimpleAuthProvider] 인증 새로고침 시작');
    
    if (authHandlerRef.current) {
      await authHandlerRef.current.refreshAuthState();
    }
  }, []);

  // 사용자 이메일 가져오기 (향후 SimpleVsCodeAuthHandler로 이동 예정)
  const fetchUserEmail = useCallback(async (): Promise<string | undefined> => {
    console.log('[SimpleAuthProvider] 사용자 이메일 가져오기 (미구현)');
    return undefined;
  }, []);

  // 컨텍스트 값
  const contextValue: SimpleAuthContextType = {
    ...authState,
    logout,
    refreshAuth,
    fetchUserEmail,
  };

  return (
    <SimpleAuthContext.Provider value={contextValue}>
      {/* FSD 아키텍처: 로딩 UI는 상위 레이어에서 isLoading 상태 확인하여 처리 */}
      {children}
    </SimpleAuthContext.Provider>
  );
};

/**
 * 간소화된 인증 컨텍스트 사용 훅
 */
export const useSimpleAuth = (): SimpleAuthContextType => {
  const context = useContext(SimpleAuthContext);
  if (!context) {
    throw new Error('useSimpleAuth must be used within a SimpleAuthProvider');
  }
  return context;
};